/**
 * Portal Login & First-Time Setup
 *
 * Provides email/phone login and a simple password-creation onboarding flow
 * for homeowners accessing the Customer Portal after contract signing.
 * UI-only — no real auth backend; simulates the experience.
 */

import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useBranding } from "@/data/BrandingContext";
import { useAppData } from "@/data/AppDataContext";
import { PortalLayout } from "@/components/portal/PortalLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { formatAddress } from "@/lib/addressUtils";
import {
  CheckCircle, Mail, Phone, Lock, ArrowRight, ShieldCheck, Eye, EyeOff,
} from "lucide-react";

type Step = "welcome" | "create-password" | "ready";
type LoginMethod = "email" | "phone";

export default function PortalLogin() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { branding } = useBranding();
  const { jobs } = useAppData();

  const token = searchParams.get("token");
  const isNewSetup = searchParams.get("setup") === "true";

  // Resolve job from token
  const job = token ? jobs.find((j) => j.id === token) : undefined;

  // State
  const [step, setStep] = useState<Step>(isNewSetup ? "welcome" : "welcome");
  const [loginMethod, setLoginMethod] = useState<LoginMethod>("email");
  const [emailInput, setEmailInput] = useState(job?.email || "");
  const [phoneInput, setPhoneInput] = useState(job?.phone || "");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      if (isNewSetup) {
        setStep("create-password");
      } else {
        // Simulate login — go to portal
        const targetJob = job || jobs[0];
        if (targetJob) {
          toast.success("Welcome back!");
          navigate(`/portal/${targetJob.id}`);
        }
      }
    }, 800);
  };

  const handleCreatePassword = () => {
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      setStep("ready");
    }, 800);
  };

  const handleEnterPortal = () => {
    const targetJob = job || jobs[0];
    toast.success("Your portal is ready!");
    navigate(`/portal/${targetJob?.id || ""}`);
  };

  return (
    <PortalLayout>
      <div className="max-w-md mx-auto py-8">
        {/* ── Welcome / Login Step ── */}
        {step === "welcome" && (
          <Card className="shadow-sm">
            <CardContent className="p-6 space-y-6">
              {/* Header */}
              <div className="text-center space-y-2">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto">
                  <ShieldCheck className="h-7 w-7 text-primary" />
                </div>
                {isNewSetup && job ? (
                  <>
                    <h2 className="text-xl font-bold text-foreground">Welcome, {job.customerName.split(" ")[0]}!</h2>
                    <p className="text-sm text-muted-foreground">
                      Your project agreement has been signed. Set up your portal to track your project.
                    </p>
                  </>
                ) : (
                  <>
                    <h2 className="text-xl font-bold text-foreground">Customer Portal</h2>
                    <p className="text-sm text-muted-foreground">
                      Sign in to view your project details and documents.
                    </p>
                  </>
                )}
              </div>

              {/* Method toggle */}
              <div className="flex gap-2">
                <Button
                  variant={loginMethod === "email" ? "default" : "outline"}
                  size="sm"
                  className="flex-1"
                  onClick={() => setLoginMethod("email")}
                >
                  <Mail className="h-3.5 w-3.5 mr-1.5" /> Email
                </Button>
                <Button
                  variant={loginMethod === "phone" ? "default" : "outline"}
                  size="sm"
                  className="flex-1"
                  onClick={() => setLoginMethod("phone")}
                >
                  <Phone className="h-3.5 w-3.5 mr-1.5" /> Phone
                </Button>
              </div>

              {/* Input */}
              <div className="space-y-2">
                <Label className="text-xs font-medium">
                  {loginMethod === "email" ? "Email Address" : "Phone Number"}
                </Label>
                {loginMethod === "email" ? (
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                  />
                ) : (
                  <Input
                    type="tel"
                    placeholder="555-123-4567"
                    value={phoneInput}
                    onChange={(e) => setPhoneInput(e.target.value)}
                  />
                )}
              </div>

              {/* Password field for returning users */}
              {!isNewSetup && (
                <div className="space-y-2">
                  <Label className="text-xs font-medium">Password</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              )}

              <Button
                className="w-full"
                onClick={handleLogin}
                disabled={isLoading || (loginMethod === "email" ? !emailInput : !phoneInput)}
              >
                {isLoading ? "Verifying…" : isNewSetup ? "Get Started" : "Sign In"}
                <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>

              {isNewSetup && (
                <p className="text-[10px] text-center text-muted-foreground">
                  By continuing, you agree to our Terms of Service and Privacy Policy.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* ── Create Password Step ── */}
        {step === "create-password" && (
          <Card className="shadow-sm">
            <CardContent className="p-6 space-y-6">
              <div className="text-center space-y-2">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto">
                  <Lock className="h-7 w-7 text-primary" />
                </div>
                <h2 className="text-xl font-bold text-foreground">Create Your Password</h2>
                <p className="text-sm text-muted-foreground">
                  Set a password so you can log in anytime to check your project status.
                </p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs font-medium">Password</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="Choose a password (6+ characters)"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-medium">Confirm Password</Label>
                  <Input
                    type="password"
                    placeholder="Re-enter your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </div>

                {/* Strength hints */}
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2 text-[11px]">
                    <CheckCircle className={`h-3 w-3 ${password.length >= 6 ? "text-success" : "text-muted-foreground/40"}`} />
                    <span className={password.length >= 6 ? "text-foreground" : "text-muted-foreground"}>At least 6 characters</span>
                  </div>
                  <div className="flex items-center gap-2 text-[11px]">
                    <CheckCircle className={`h-3 w-3 ${password && password === confirmPassword ? "text-success" : "text-muted-foreground/40"}`} />
                    <span className={password && password === confirmPassword ? "text-foreground" : "text-muted-foreground"}>Passwords match</span>
                  </div>
                </div>
              </div>

              <Button
                className="w-full"
                onClick={handleCreatePassword}
                disabled={isLoading || password.length < 6 || password !== confirmPassword}
              >
                {isLoading ? "Setting up…" : "Create Account"}
                <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* ── Ready Step ── */}
        {step === "ready" && (
          <Card className="shadow-sm">
            <CardContent className="p-6 space-y-6">
              <div className="text-center space-y-3">
                <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto">
                  <CheckCircle className="h-8 w-8 text-success" />
                </div>
                <h2 className="text-xl font-bold text-foreground">You're All Set!</h2>
                <p className="text-sm text-muted-foreground">
                  Your portal account has been created. You can now view your project details, documents, and track progress.
                </p>
              </div>

              {job && (
                <div className="rounded-lg border bg-muted/20 p-4 space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">Your Project</p>
                  <p className="text-sm font-semibold">{job.projectType} — {formatAddress(job.address)}</p>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px] h-5 bg-primary/5 text-primary border-primary/20">
                      <ShieldCheck className="h-3 w-3 mr-1" /> Contract Signed
                    </Badge>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Button className="w-full" onClick={handleEnterPortal}>
                  View My Project
                  <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
                <p className="text-[10px] text-center text-muted-foreground">
                  Bookmark this page to easily access your portal anytime.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </PortalLayout>
  );
}
